import React, { JSX, useEffect, useState } from "react";
import { Box, Button, InputAdornment, Table, TableHead, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import produce from "immer";
import { round } from "mathjs";
import { push } from "connected-react-router";
import Typography from "@mui/material/Typography";
import { t } from "../../../components/translate";
import { BillingPackage } from "../../../interfaces/BillingPackages";
import Panel from "../../../components/Panel";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { getNextBillNumber, initDraftAction } from "../../../actions/bill";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { createTemplateDraft } from "../../../actions/helpers/billHelper";
import { TrackingNumType } from "../../../interfaces/Accounts";
import BillingPackageStatusChip from "./BillingPackageStatusChip";
import { paymentOptionsEnum } from "../Editor/BillSummary";
import {
  BillingPackageStatusType,
  distributePayingAmount,
  getBillingPackageTotalAmounts,
  getSumInfo,
  getTaxAmtWithRates,
  ITEM_TYPES,
  setPayingAmountByDefaultOnNonPackagedItems
} from "../../../helpers/billingPackages";
import IndividualRow from "./IndividualRow";
import AdditionalItems from "./AdditionalItems";
import SummaryItemRow from "./SummaryItemRow";
import AlreadyPaidItems from "./AlreadyPaidItems";
import BillingPackageCancel from "./BillingPackageCancel";
import { createBillingPackageDraft } from "../../../slices/billPackageSlice";
import { notificationAdd } from "../../../actions/notification";

interface Props {
  onClose: () => void;
  data: BillingPackage;
}

function BillingPackageEditPanel({ onClose, data }: Props): JSX.Element {
  const [formData, setFormData] = useState(data);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [totalPayingAmount, setTotalPayingAmount] = useState(0);
  const rc = useCurrentResourceCentre();
  const userContext = useAppSelector((state) => state?.userContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useAppDispatch();

  const allBillItems = [
    ...formData.document.packaged,
    ...formData.document.nonPackaged,
    ...formData.document.additional
  ];

  const nonPackagedItems = [...formData.document.nonPackaged, ...formData.document.additional];
  const { totalAmount, totalDueAmount, totalPaidAmount, totalNetAmount } =
    getBillingPackageTotalAmounts(allBillItems);

  const requiredAmountToCreateBill = nonPackagedItems
    .filter((item) => !item.isPaid)
    .reduce((acc, cur) => acc + Number(cur.netAmount || 0), 0);

  const remainingTotalDueAmount = allBillItems
    .filter((item) => !item.isPaid)
    .reduce((prev, cur) => prev + Number(cur.netAmount || 0) - cur.paidAmount, 0);

  useEffect(() => {
    setFormData(setPayingAmountByDefaultOnNonPackagedItems(data));
    setTotalPayingAmount(requiredAmountToCreateBill);
  }, [data]);

  return (
    <Panel
      onClose={onClose}
      titleJsx={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%"
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Typography>Edit package and create bill</Typography>
            <BillingPackageStatusChip status={formData.status} />
          </Box>
          {![BillingPackageStatusType.COMPLETED, BillingPackageStatusType.CANCELED].includes(
            formData.status
          ) && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setOpenCancelDialog(true);
              }}
            >
              Cancel Package
            </Button>
          )}
        </Box>
      }
      wrapperStyle={{ minWidth: "1200px" }}
      footer={
        ![BillingPackageStatusType.COMPLETED, BillingPackageStatusType.CANCELED].includes(
          formData.status
        ) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              width: "100%",
              mx: 4
            }}
          >
            <Button onClick={() => onClose()}>{t("global.cancel")}</Button>
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              type="submit"
              form="packageForm"
              disabled={
                totalPayingAmount === 0 ||
                totalPayingAmount < requiredAmountToCreateBill ||
                isSubmitting
              }
            >
              Save and Create Bill
            </LoadingButton>
          </Box>
        )
      }
    >
      <Box
        sx={{
          px: 5,
          py: 2,
          overflowY: "auto",
          height: "calc(100vh - 100px)"
        }}
        id="packageForm"
        component="form"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            setIsSubmitting(true);

            const billDraft = createTemplateDraft(
              userContext,
              formData.client,
              rc?.settings,
              false
            );
            const updatedFormData = produce(formData, (draft) => {
              const order = ["nonPackaged", "additional", "packaged"];

              order.forEach((section) => {
                draft.document[section].forEach((item) => {
                  if (item.payingAmount > 0) {
                    // eslint-disable-next-line no-param-reassign
                    item.isPaid = Number(item.dueAmount) === 0;
                  }
                });
              });
            });

            billDraft.billItems = produce(formData.document, (draftBillItems) => {
              const order = ["nonPackaged", "additional", "packaged"];
              const result = [];
              let sNo = 1;

              order.forEach((section) => {
                draftBillItems[section]
                  .filter((item) => item.payingAmount > 0)
                  .forEach((item) => {
                    result.push({
                      ...item,
                      // eslint-disable-next-line no-plusplus
                      sNo: sNo++,
                      // setting discount = 0 as we dont want to show disount on actual bill
                      discountAmt: 0,
                      discountPercent: 0,
                      isPaid: Number(item.dueAmount) === 0,
                      grossTotal: item.payingAmount,
                      perUnit: item.payingAmount / item.quantity
                    });
                  });
              });

              return result;
            });
            const sumInfo = getSumInfo(billDraft.billItems);
            billDraft.paymentInfo = {
              paid: true,
              paidAmount: round(sumInfo.totalAmount, 2),
              paymentBreakdown: [{ method: paymentOptionsEnum.cash, amount: sumInfo.totalAmount }],
              paymentDistribution: {
                additionalPayment: round(sumInfo.totalAmount, 2),
                paymentFromBalance: 0
              }
            };
            billDraft.summary = {
              inWords: "",
              discount: 0,
              taxAmount: round(sumInfo.taxAmount, 2),
              discountPct: Number(formData.discountPct),
              roundOffAmt: 0,
              totalAmount: round(sumInfo.totalAmount, 2),
              taxableAmount: sumInfo.taxableAmount,
              taxAmtWithRates: getTaxAmtWithRates(billDraft.billItems),
              totalPerUnitTimesQty: round(sumInfo.taxableAmount, 2)
            };
            dispatch(createBillingPackageDraft(updatedFormData));
            await dispatch(initDraftAction(billDraft));
            await dispatch(getNextBillNumber(TrackingNumType.SALES, billDraft.issueDate));
            dispatch(push("/billing/new?isFromBillingPackage=yes"));
          } catch (err) {
            dispatch(
              notificationAdd({
                id: new Date().getUTCMilliseconds(),
                variant: "error",
                message: err?.message || "Something Went Wrong!.",
                autoTimeout: true
              })
            );
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Teeth</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Price</TableCell>
                <TableCell align="right">Due Amt.</TableCell>
                <TableCell align="right">Paid Amt.</TableCell>
                <TableCell>Dis. %</TableCell>
                <TableCell>Dis. Amt</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell align="right" />
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {formData?.document?.nonPackaged
                ?.map((item, index) => ({ ...item, index }))
                ?.filter((item) => !item.isPaid)
                ?.map(({ index, ...item }) => (
                  <IndividualRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${item.description}-${index}`}
                    item={item}
                    index={index}
                    items={formData}
                    itemType={ITEM_TYPES.NON_PACKAGED}
                    setFormData={setFormData}
                    setTotalPayingAmount={setTotalPayingAmount}
                  />
                ))}
              {formData?.document?.packaged
                ?.filter((item) => !item.isPaid)
                ?.map((item, i) => (
                  <IndividualRow
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${item.description}-${i}`}
                    item={item}
                    index={i}
                    items={formData}
                    setFormData={setFormData}
                    itemType={ITEM_TYPES.PACKAGED}
                    setTotalPayingAmount={setTotalPayingAmount}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {![BillingPackageStatusType.COMPLETED, BillingPackageStatusType.CANCELED].includes(
          formData.status
        ) && (
          <AdditionalItems
            formData={formData}
            itemType={ITEM_TYPES.ADDITIONAL}
            setFormData={setFormData}
          />
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            flexDirection: "column",
            gap: 1,
            p: 2
          }}
        >
          <SummaryItemRow label="Total Amount" value={totalAmount.toFixed(2)} />
          <SummaryItemRow label="Net Amount" value={totalNetAmount.toFixed(2)} />
          <SummaryItemRow label="Previously Paid Amount" value={totalPaidAmount.toFixed(2)} />
          <SummaryItemRow label="Due Amount" value={totalDueAmount.toFixed(2)} />
          {[BillingPackageStatusType.PARTIALLY_PAID, BillingPackageStatusType.UNPAID].includes(
            formData.status
          ) && (
            <SummaryItemRow
              label="Paying Amount"
              jsxValue={
                <TextField
                  value={totalPayingAmount}
                  placeholder="Total"
                  slotProps={{
                    htmlInput: { sx: { textAlign: "right", pr: 1 } },
                    input: {
                      startAdornment: <InputAdornment position="start">Rs.</InputAdornment>
                    }
                  }}
                  size="small"
                  onChange={(event) => {
                    const { value } = event.target;

                    if (!/^\d*\.?\d*$/.test(value)) return;

                    const numericValue = Number(value || 0);

                    if (numericValue > remainingTotalDueAmount) {
                      const max = remainingTotalDueAmount.toFixed(2);

                      setTotalPayingAmount(max);

                      setFormData((prev) => ({
                        ...prev,
                        document: distributePayingAmount(prev.document, remainingTotalDueAmount)
                      }));

                      return;
                    }

                    setTotalPayingAmount(value);

                    setFormData((prev) => ({
                      ...prev,
                      document: distributePayingAmount(prev.document, numericValue)
                    }));
                  }}
                  variant="outlined"
                />
              }
            />
          )}
        </Box>
        <AlreadyPaidItems formData={formData} />
      </Box>
      {formData.id && (
        <BillingPackageCancel
          onCancel={() => {
            setOpenCancelDialog(false);
            onClose();
          }}
          open={openCancelDialog}
          id={formData.id}
        />
      )}
    </Panel>
  );
}

export default BillingPackageEditPanel;
